import React from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import { base_url } from "Backend/api";
import { resolveHomeCardOrdersCount } from "../../../utils/storeOrdersCount";

function resolveImage(restaurant) {
  const rawUrl = restaurant?.image_url;
  if (rawUrl && /^https?:\/\//i.test(String(rawUrl))) {
    return String(rawUrl);
  }
  if (rawUrl && String(rawUrl).startsWith("/")) {
    return `${String(base_url).replace(/\/$/, "")}${rawUrl}`;
  }
  if (restaurant?.image) {
    const p = String(restaurant.image).replace(/^\//, "");
    return `${base_url}/storage/app/public/${p}`;
  }
  return "/assets/images/logo.jpeg";
}

function categoryLabel(restaurant) {
  if (Array.isArray(restaurant?.categories) && restaurant.categories.length > 0) {
    const sorted = [...restaurant.categories].sort(
      (a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0)
    );
    return sorted
      .map((c) => c?.name)
      .filter(Boolean)
      .join("، ");
  }
  const t =
    restaurant?.type ??
    restaurant?.category ??
    restaurant?.category_name ??
    "";
  return String(t).replace(/\s*,\s*/g, "، ").trim();
}

/**
 * كارت مطعم موحّد للصفحة الرئيسية (جديد / توصيل سريع / كل المطاعم).
 * الحقول اختيارية حسب شكل الـ API.
 */
const RestaurantHomeCard = ({
  restaurant,
  isFavorite,
  onCardClick,
  onFavoriteClick,
}) => {
  const slug = restaurant?.slug;
  const ratingAvg =
    restaurant?.rating?.average ?? restaurant?.average_rating ?? null;
  const reviewsCount =
    restaurant?.rating?.reviews_count ??
    restaurant?.reviews_count ??
    0;
  const showRating =
    ratingAvg != null && ratingAvg !== "" && !Number.isNaN(Number(ratingAvg));

  const category = categoryLabel(restaurant);

  const minOrder = restaurant?.minimum_order;
  const hasMin = minOrder != null && minOrder !== "";

  const isActive =
    restaurant?.is_open_now === true ||
    restaurant?.is_open_now === 1 ||
    restaurant?.is_active === true ||
    restaurant?.is_active === 1 ||
    restaurant?.status === "active" ||
    restaurant?.status === "open";

  const orderTotal = resolveHomeCardOrdersCount(restaurant);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => slug && onCardClick?.(slug)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          slug && onCardClick?.(slug);
        }
      }}
      className="cursor-pointer w-full rounded-[8px] border border-gray-200 bg-white shadow-sm outline-none transition duration-300 focus-visible:ring-2 focus-visible:ring-emerald-500 group overflow-hidden font-[cairo]"
      dir="rtl"
    >
      <div className="relative h-[100px] overflow-hidden sm:h-[168px]">
        <Image
          src={resolveImage(restaurant)}
          alt={restaurant?.name ?? ""}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            slug && onFavoriteClick?.(slug);
          }}
          className="absolute end-2 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition hover:bg-gray-50"
          aria-label="المفضلة"
        >
          <Icon
            name="Heart"
            size={14}
            color={isFavorite ? "#ef4444" : "#9ca3af"}
            fill={isFavorite ? "#ef4444" : "none"}
          />
        </button>

        <div className="absolute end-2 top-2 z-10 flex max-w-[55%] flex-col items-end gap-1 pt-1">
          {restaurant?.offers?.slice(0, 2).map((offer, index) => (
            <span
              key={offer?.id ?? index}
              className="flex max-w-full items-center gap-1 rounded-s-lg bg-[#F93209] py-1 pe-2 ps-2 text-[9px] font-bold leading-tight text-white shadow"
            >
              <span className="line-clamp-2 break-words text-end">
                {offer?.title}
              </span>
              <img
                src="/assets/images/badd.svg"
                className="h-3.5 w-3.5 shrink-0 opacity-95"
                alt=""
              />
            </span>
          ))}
        </div>

        {showRating && (
          <div className="absolute bottom-[-2px] start-[-8px] z-10 flex items-center gap-1 rounded-full bg-[#3AD900] px-2 py-0.5 shadow-sm">
            <Icon
              name="Star"
              size={12}
              color="#FF9F06"
              fill="#FF9F06"
              className="shrink-0 mr-1"
            />
            <span className="text-[10px] font-bold text-white">
              {ratingAvg}
              <span className="font-medium opacity-95">
                {" "}
                ({reviewsCount})
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="space-y-2 p-3 sm:p-3">
        <h3 className="line-clamp-1 text-right text-[13px] font-bold font-[cairo] text-gray-900 sm:text-base">
          {restaurant?.name}
        </h3>

        {/* يمين: يبدأ من — شمال السطر: التصنيف (محاذاة يسار جغرافية داخل المساحة) */}
        <div className="flex items-start justify-between gap-2 text-[11px] sm:text-xs">
          {hasMin ? (
            <span className="shrink-0 text-right font-medium text-gray-800">
              يبدأ من {minOrder} ل.س
            </span>
          ) : (
            <span className="shrink-0" />
          )}
          <span className="line-clamp-1 min-w-0 flex-1 text-left text-gray-600">
            {category || "\u00A0"}
          </span>
        </div>

        {/* إحداثيات الشاشة: يسار = الحالة، يمين = عدد الطلبات */}
        <div
          dir="rtl"
          className="flex flex-row items-center justify-between gap-2  text-[10px] sm:text-[11px]"
        >
          <span
            className={`flex shrink-0 items-center gap-1.5 font-medium ${
              isActive ? "text-[#00B200]" : "text-[#F93209]"
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                isActive ? "bg-[#00B200]" : "bg-[#F93209]"
              }`}
            />
            {isActive ? (restaurant?.status_label ?? "نشط") : "غير نشط"}
          </span>
          <span className="flex min-w-0 items-center gap-1 text-gray-600">
            <img src="/assets/images/bbagg.svg" className="h-3 w-3" alt="bag" />
            {orderTotal != null ? (
              <span className="truncate">
                {orderTotal} طلب
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RestaurantHomeCard;
